import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Edit2, Save, X, UserPlus, Calendar, Phone, Mail, Heart, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FamilyManager = ({ patientId }) => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    relationship: 'figlio',
    birth_date: '',
    fiscal_code: '',
    phone: '',
    email: '',
    notes: ''
  });

  const RELATIONSHIPS = [
    { value: 'padre', label: 'Padre', icon: '👨' },
    { value: 'madre', label: 'Madre', icon: '👩' },
    { value: 'figlio', label: 'Figlio', icon: '👦' },
    { value: 'figlia', label: 'Figlia', icon: '👧' },
    { value: 'fratello', label: 'Fratello', icon: '👨‍👦' },
    { value: 'sorella', label: 'Sorella', icon: '👩‍👧' },
    { value: 'nonno', label: 'Nonno', icon: '👴' },
    { value: 'nonna', label: 'Nonna', icon: '👵' },
    { value: 'nipote_m', label: 'Nipote (M)', icon: '🧒' },
    { value: 'nipote_f', label: 'Nipote (F)', icon: '👧' },
    { value: 'suocero', label: 'Suocero', icon: '👨‍🦳' },
    { value: 'suocera', label: 'Suocera', icon: '👩‍🦳' },
    { value: 'genero', label: 'Genero', icon: '👨‍💼' },
    { value: 'nuora', label: 'Nuora', icon: '👩‍💼' },
    { value: 'cognato', label: 'Cognato', icon: '👨' },
    { value: 'cognata', label: 'Cognata', icon: '👩' },
    { value: 'coniuge', label: 'Coniuge', icon: '💑' },
    { value: 'convivente', label: 'Convivente', icon: '💑' },
    { value: 'altro', label: 'Altro Familiare', icon: '👤' }
  ];

  useEffect(() => {
    fetchFamilyMembers();
  }, [patientId]);

  const fetchFamilyMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/patient/family`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFamilyMembers(data.family_members || []);
      }
    } catch (error) {
      console.error('Error fetching family:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingMember 
        ? `${API}/api/patient/family/${editingMember.id}`
        : `${API}/api/patient/family`;
      
      const res = await fetch(url, {
        method: editingMember ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        fetchFamilyMembers();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving family member:', error);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questo familiare?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/patient/family/${memberId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchFamilyMembers();
      }
    } catch (error) {
      console.error('Error deleting family member:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      relationship: 'figlio',
      birth_date: '',
      fiscal_code: '',
      phone: '',
      email: '',
      notes: ''
    });
    setEditingMember(null);
    setShowAddModal(false);
  };

  const startEdit = (member) => {
    setFormData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      relationship: member.relationship || 'figlio',
      birth_date: member.birth_date || '',
      fiscal_code: member.fiscal_code || '',
      phone: member.phone || '',
      email: member.email || '',
      notes: member.notes || ''
    });
    setEditingMember(member);
    setShowAddModal(true);
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getRelationshipIcon = (relationship) => {
    const rel = RELATIONSHIPS.find(r => r.value === relationship);
    return rel?.icon || '👤';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A9FCB]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB', fontFamily: "'Lora', Georgia, serif"}}>
            Nucleo Familiare
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Gestisci i membri della tua famiglia per gli abbonamenti condivisi
          </p>
        </div>
        {familyMembers.length > 0 && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:opacity-90"
            style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
          data-testid="add-family-member-btn"
        >
          <UserPlus className="w-4 h-4" />
          Aggiungi Familiare
        </button>
        )}
      </div>

      {/* Info Box */}
      <div className="p-4 rounded-xl border" style={{background: '#E8F4FD', borderColor: '#2A9FCB'}}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" style={{color: '#2A9FCB'}} />
          <div>
            <p className="text-sm font-medium" style={{color: '#2A9FCB'}}>Abbonamento Famiglia</p>
            <p className="text-xs text-gray-600 mt-1">
              Con un abbonamento famiglia, puoi aggiungere fino a 5 membri che potranno usufruire di tutti i servizi inclusi.
              Ogni membro può prenotare visite autonomamente.
            </p>
          </div>
        </div>
      </div>

      {/* Family Members Grid */}
      {familyMembers.length === 0 ? (
        <div className="text-center py-16 bg-gradient-to-br from-[#E8F4FD] to-white rounded-2xl border-2 border-dashed border-[#2A9FCB]/30">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#2A9FCB]/10 flex items-center justify-center">
            <Users className="w-10 h-10 text-[#2A9FCB]" />
          </div>
          <h3 className="text-xl font-semibold mb-2" style={{color: '#2A9FCB'}}>
            Crea il tuo Nucleo Familiare
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Aggiungi i membri della tua famiglia per condividere l'abbonamento e gestire insieme la salute dei vostri occhi.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-all hover:opacity-90 hover:scale-105"
            style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
            data-testid="create-family-btn"
          >
            <UserPlus className="w-5 h-5" />
            Crea una Famiglia
          </button>
          <p className="text-xs text-gray-400 mt-4">
            Puoi aggiungere fino a 5 membri familiari
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {familyMembers.map((member) => (
            <div 
              key={member.id} 
              className="bg-white rounded-xl border p-5 hover:shadow-lg transition-all"
              data-testid={`family-member-${member.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl" style={{background: '#E8F4FD'}}>
                    {getRelationshipIcon(member.relationship)}
                  </div>
                  <div>
                    <h3 className="font-semibold" style={{color: '#2A9FCB'}}>
                      {member.first_name} {member.last_name}
                    </h3>
                    <p className="text-xs text-gray-500 capitalize">
                      {RELATIONSHIPS.find(r => r.value === member.relationship)?.label || member.relationship}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => startEdit(member)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Modifica"
                  >
                    <Edit2 className="w-4 h-4 text-gray-400" />
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                    title="Rimuovi"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                {member.birth_date && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(member.birth_date).toLocaleDateString('it-IT')} ({calculateAge(member.birth_date)} anni)</span>
                  </div>
                )}
                {member.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{member.phone}</span>
                  </div>
                )}
                {member.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{member.email}</span>
                  </div>
                )}
                {member.fiscal_code && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Heart className="w-4 h-4" />
                    <span className="font-mono text-xs">{member.fiscal_code}</span>
                  </div>
                )}
              </div>

              {member.notes && (
                <p className="mt-3 text-xs text-gray-500 border-t pt-3">{member.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold" style={{color: '#2A9FCB'}}>
                {editingMember ? 'Modifica Familiare' : 'Aggiungi Familiare'}
              </h3>
              <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    placeholder="Nome"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    placeholder="Cognome"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parentela *</label>
                <select
                  required
                  value={formData.relationship}
                  onChange={(e) => setFormData({...formData, relationship: e.target.value})}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                >
                  {RELATIONSHIPS.map(rel => (
                    <option key={rel.value} value={rel.value}>{rel.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                  <input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                  <input
                    type="text"
                    value={formData.fiscal_code}
                    onChange={(e) => setFormData({...formData, fiscal_code: e.target.value.toUpperCase()})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent font-mono"
                    placeholder="RSSMRA85T10H501X"
                    maxLength={16}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                    placeholder="email@esempio.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] focus:border-transparent"
                  rows={2}
                  placeholder="Eventuali note o informazioni aggiuntive..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 py-3 border rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 rounded-lg font-medium text-white transition-all hover:opacity-90 flex items-center justify-center gap-2"
                  style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                >
                  <Save className="w-4 h-4" />
                  {editingMember ? 'Salva Modifiche' : 'Aggiungi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyManager;
